import { Box, Paper, Grid, Typography, Button, TextField, IconButton } from '@mui/material';
import React, { useState, useRef, useEffect } from 'react';
import { CustomField, SectionProps } from '../../../Admin/PreboardingConfiguration/CandidateDetailsConfiguration/CandidateDetailsConfiguration';
import FieldDisplay from './FieldDisplay';
import { theme } from '../../../../../theme';
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EmploymentDetailsSection from '../../../Admin/PreboardingConfiguration/CandidateDetailsConfiguration/EmployeementDetailsSection';
import { deleteEmploymentHistory } from '../candidatePreboardingApi';
import { useParams } from 'react-router-dom';
import { useSnackbar } from '../../../../../Context/SnackbarContext';
import { useDeleteDialog } from '../../../../../Context/DeleteConfirmationContext';
import { useCandidatePreboarding } from '../CandidatePreboardingContext';

interface CandidateEmployementDetailsProps {
    retrievedFields: CustomField[];
    sectionData: SectionProps | undefined;
    fieldValues: { [key: number]: any };
    fieldErrors: { [key: string]: string };
    setFieldErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
    onFieldChange: (fieldId: number, value: any) => void;
    employmentHistory: EmploymentRow[];
    setEmploymentHistory: React.Dispatch<React.SetStateAction<EmploymentRow[]>>;
}

export interface EmploymentRow {
    id: number; // frontend local ID
    employmentId?: number; // backend EmploymentId
    employer: string;
    designation: string;
    startDate: string;
    endDate: string;
}

const CandidateEmployementDetails: React.FC<CandidateEmployementDetailsProps> = ({
    retrievedFields,
    sectionData,
    fieldValues,
    fieldErrors,
    onFieldChange,
    employmentHistory,
    setEmploymentHistory,
    setFieldErrors
}) => {

    const endRef = useRef<HTMLDivElement | null>(null);
    const isCandidate = !!sessionStorage.getItem("candidate_session");
    const { tenant } = useParams<{ tenant: string }>();
    const { showSnackbar } = useSnackbar();
    const { openDeleteDialog } = useDeleteDialog();
    const { candidateData } = useCandidatePreboarding();
    const isPendingDocumentVerification = candidateData?.preboardingStatusKey === "PendingDocumentVerification" || candidateData?.preboardingStatusKey === "BGVPending" || candidateData?.preboardingStatusKey === "Hired" || candidateData?.preboardingStatusKey === "BGVRejected";

    // Add new employment row
    const handleAddEmployment = () => {
        setEmploymentHistory((prev) => [
            ...prev,
            { id: Date.now() + Math.random(), employer: "", designation: "", startDate: "", endDate: "" }
        ]);

        // Scroll to bottom after a new row is added
        setTimeout(() => {
            endRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 50); // slight delay to ensure DOM updated
    };

    // Update a specific field in a row
    const handleChange = (id: number, field: keyof EmploymentRow, value: string) => {
        setEmploymentHistory((prev) => {
            const updatedRows = prev.map((row) => (row.id === id ? { ...row, [field]: value } : row));
            const rowIndex = updatedRows.findIndex((row) => row.id === id);
            setFieldErrors((prevErrors) => {
                const updatedErrors = { ...prevErrors };
                if (field === "startDate") {
                    delete updatedErrors[`employment_startDate_${rowIndex}`];
                    delete updatedErrors[`employment_dateOrder_${rowIndex}`];
                } else if (field === "endDate") {
                    delete updatedErrors[`employment_endDate_${rowIndex}`];
                    delete updatedErrors[`employment_dateOrder_${rowIndex}`];
                }
                return updatedErrors;
            });
            return updatedRows;
        });
    };

    // Delete a row
    const handleDelete = (id: number) => {
        setEmploymentHistory((prev) => {
            const updatedRows = prev.filter((row) => row.id !== id);
            // Remove errors for the deleted row
            setFieldErrors((prevErrors) => {
                const updatedErrors = { ...prevErrors };
                // Find the index of the deleted row in the previous array
                const deletedIndex = prev.findIndex((row) => row.id === id);
                delete updatedErrors[`employment_startDate_${deletedIndex}`];
                delete updatedErrors[`employment_endDate_${deletedIndex}`];
                delete updatedErrors[`employment_dateOrder_${deletedIndex}`];
                return updatedErrors;
            });
            return updatedRows;
        });
    };

    const confirmDelete = async (employmentId: number) => {
        if (!employmentId) {
            showSnackbar("Invalid Employment ID", "error");
            return;
        }
        const payLoad = {
            EmploymentId: employmentId
        }

        try {
            const response = await deleteEmploymentHistory(tenant, payLoad, isCandidate);
            if (response) {
                showSnackbar(response.Message, response.Status ? 'success' : 'error');
                if (response.Status) {
                    setEmploymentHistory((prev) => prev.filter((row) => row.employmentId !== employmentId));
                }
            }
        } catch (error) {
            console.log(error)
        }
    }

    return (
        <Box>
            <Typography variant="subtitle2" color="#101828" mb={1}>
                {sectionData?.sectionName}
            </Typography>
            <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, maxHeight: "400px", overflowY: "auto" }}>
                <Grid container spacing={2}>
                    {/* Existing retrieved fields */}
                    {retrievedFields
                        .filter(
                            (field) =>
                                field.sectionName === sectionData?.sectionName &&
                                field.name.toLowerCase() !== "has full time experience"
                        )
                        .reduce((acc: any[][], curr, i) => {
                            if (i % 4 === 0) acc.push([curr]);
                            else acc[acc.length - 1].push(curr);
                            return acc;
                        }, [])
                        .map((fieldPair, rowIndex) => (
                            <Grid container item spacing={2} key={rowIndex}>
                                {fieldPair.map((field, colIndex) => (
                                    <Grid item xs={12} md={3} key={colIndex}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <FieldDisplay
                                                field={field}
                                                value={fieldValues[field.customFieldId]}
                                                error={fieldErrors[field.customFieldId]}
                                                onChange={onFieldChange}
                                            />
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        ))}

                    {/* Dynamic employment rows */}
                    {employmentHistory.map((row, index) => (
                        <Grid
                            container
                            spacing={2}
                            alignItems="center"
                            key={row.id}
                            sx={{ marginBottom: 1, marginTop: 1, marginLeft: 1 }}
                        >
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label={`Employer ${index + 1}`}
                                    value={row.employer}
                                    onChange={(e) => handleChange(row.id, "employer", e.target.value)}
                                    fullWidth
                                    size="small"
                                    autoComplete='off'
                                    error={Boolean(fieldErrors[`employment_employer_${String(index)}`])}
                                    helperText={fieldErrors[`employment_employer_${String(index)}`]}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="Designation"
                                    value={row.designation}
                                    onChange={(e) => handleChange(row.id, "designation", e.target.value)}
                                    fullWidth
                                    size="small"
                                    autoComplete='off'
                                    error={Boolean(fieldErrors[`employment_designation_${String(index)}`])}
                                    helperText={fieldErrors[`employment_designation_${String(index)}`]}
                                />
                            </Grid>

                            <Grid item xs={12} md={isPendingDocumentVerification ? 3 : 2.5}>
                                <TextField
                                    type="date"
                                    label="Start Date"
                                    value={row.startDate}
                                    onChange={(e) => handleChange(row.id, "startDate", e.target.value)}
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    autoComplete='off'
                                    error={Boolean(fieldErrors[`employment_startDate_${String(index)}`])}
                                    helperText={fieldErrors[`employment_startDate_${String(index)}`]}
                                />
                            </Grid>

                            <Grid item xs={12} md={isPendingDocumentVerification ? 3 : 2.5}>
                                <TextField
                                    type="date"
                                    label="End Date"
                                    value={row.endDate}
                                    onChange={(e) => handleChange(row.id, "endDate", e.target.value)}
                                    fullWidth
                                    size="small"
                                    autoComplete='off'
                                    InputLabelProps={{ shrink: true }}
                                    error={Boolean(fieldErrors[`employment_endDate_${String(index)}`]) || Boolean(fieldErrors[`employment_dateOrder_${String(index)}`])}
                                    helperText={
                                        fieldErrors[`employment_endDate_${String(index)}`]
                                            ? fieldErrors[`employment_endDate_${String(index)}`]
                                            : fieldErrors[`employment_dateOrder_${String(index)}`]
                                    }
                                />
                            </Grid>
                            {
                                isPendingDocumentVerification ? (
                                    <></>
                                ) : (
                                    <Grid item xs={12} md={1} display="flex" justifyContent="center">
                                        <IconButton color="error" onClick={
                                            () => {
                                                if (row.employmentId) {
                                                    openDeleteDialog(() => confirmDelete(row.employmentId!));
                                                } else {
                                                    handleDelete(row.id);
                                                }
                                            }
                                        }>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Grid>
                                )
                            }


                        </Grid>
                    ))}

                    {/* Invisible marker to scroll into view */}
                    <div ref={endRef} />

                    {/* Add button */}
                    {
                        isPendingDocumentVerification ? (
                            <></>
                        ) : (
                            <Grid item xs={12} display="flex" alignItems="center">
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddEmployment}
                                    sx={{
                                        color: theme.palette.primary.main,
                                        borderColor: theme.palette.primary.main,
                                        ml: "auto",
                                    }}
                                >
                                    Add Previous Employment
                                </Button>
                            </Grid>
                        )
                    }

                </Grid>
            </Paper>
        </Box>
    );
};

export default CandidateEmployementDetails;
